import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Download, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATALOG_META = {
  windows: {
    eyebrow: "48-Page Edition",
    title: "ViewLux Catalog",
    subtitle: "2026",
    description:
      "48 pages of windows, sliding systems, profiles, finishes, and architectural details.",
    cta: "Get the Catalog",
    fallback: "/viewlux/viewlux-catalog-2026.pdf",
    fallbackName: "ViewLux-Catalog-2026.pdf",
  },
  doors: {
    eyebrow: "The Doors Edition",
    title: "ViewLux Doors Catalog",
    subtitle: "2026",
    description:
      "120+ designs across cast aluminum, wood, stainless steel, and interior series. Every door built-to-order.",
    cta: "Get the Doors Catalog",
    fallback: "/viewlux/docs/viewlux-doors-2026.pdf",
    fallbackName: "ViewLux-Doors-2026.pdf",
  },
};

export default function CatalogDialog({ open, onClose, kind = "windows" }) {
  const meta = CATALOG_META[kind] || CATALOG_META.windows;
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "Architect",
    alsoDoors: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [downloads, setDownloads] = useState(null);

  // Reset state whenever the dialog is opened / kind switches
  useEffect(() => {
    if (open) {
      setDownloads(null);
      setForm((f) => ({ ...f, alsoDoors: false }));
    }
  }, [open, kind]);

  if (!open) return null;

  const handleChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }

    // Decide which catalog(s) to request
    let catalog = kind;
    if (kind === "windows" && form.alsoDoors) catalog = "both";

    const payload = {
      name: form.name,
      email: form.email,
      company: form.company,
      role: form.role,
      catalog,
    };

    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/catalog/download`, payload);
      const list =
        res?.data?.download_urls && res.data.download_urls.length
          ? res.data.download_urls
          : [
              {
                url: res?.data?.download_url || meta.fallback,
                name: meta.fallbackName,
                label: meta.title,
              },
            ];
      setDownloads(list);
      // Trigger downloads sequentially
      list.forEach((item, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = item.url;
          a.download = item.name || "ViewLux-Catalog.pdf";
          a.target = "_blank";
          a.rel = "noreferrer";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }, i * 350);
      });
      toast.success(
        list.length > 1
          ? "Both catalogs are downloading. Enjoy!"
          : "Your catalog is ready. Enjoy!"
      );
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail[0]?.msg
          : null) ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Invalid submission.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setDownloads(null);
    onClose?.();
  };

  return (
    <div
      data-testid="catalog-dialog"
      data-catalog-kind={kind}
      className="fixed inset-0 z-[2000] flex items-end md:items-center justify-center p-0 md:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#0a1a17]/80 backdrop-blur-sm animate-fade-up"
        onClick={closeAndReset}
      />
      <div className="relative w-full max-w-lg bg-cream shadow-2xl rounded-none md:rounded-sm overflow-hidden animate-fade-up">
        <div className="relative px-7 md:px-10 pt-10 pb-8 bg-[#0f2420] text-white">
          <button
            data-testid="catalog-dialog-close"
            onClick={closeAndReset}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/5 border border-[#c99658]/30 text-[#e2b880] hover:bg-[#c99658]/15"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 text-[10px] tracking-[0.5em] uppercase font-raleway text-[#e2b880]">
            <span className="w-6 h-px bg-[#c99658]" />
            {meta.eyebrow}
          </div>
          <h3 className="heading-lg mt-6 text-[28px] md:text-[34px] leading-[1.05]">
            Download the{" "}
            <em className="italic font-light text-[#e2b880]">{meta.title}</em>{" "}
            <span className="opacity-80">{meta.subtitle}</span>
          </h3>
          <p className="mt-4 text-white/70 text-[14px] font-nunito">
            {meta.description}
          </p>
        </div>

        {downloads ? (
          <div className="p-7 md:p-10 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-[#c99658]" />
            <h4 className="heading-lg text-[22px] mt-4 text-[#0f2420]">
              {downloads.length > 1
                ? "Your catalogs are downloading"
                : "Your catalog is downloading"}
            </h4>
            <p className="mt-3 text-neutral-600 text-[14px] font-nunito">
              If nothing opened, tap a button below.
            </p>
            <div className="mt-7 flex flex-col gap-3 items-center">
              {downloads.map((d) => (
                <a
                  key={d.url}
                  data-testid={`catalog-redownload-${d.url.includes("doors") ? "doors" : "windows"}`}
                  href={d.url}
                  download={d.name}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-pill btn-primary inline-flex"
                >
                  <Download className="w-4 h-4" />
                  {d.label || "Open Catalog PDF"}
                </a>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 md:p-10 space-y-4">
            <Field
              label="Full Name"
              required
              value={form.name}
              onChange={handleChange("name")}
              testId="catalog-field-name"
            />
            <Field
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={handleChange("email")}
              testId="catalog-field-email"
            />
            <Field
              label="Company / Studio"
              value={form.company}
              onChange={handleChange("company")}
              testId="catalog-field-company"
            />
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.32em] font-raleway text-neutral-600 mb-2">
                I am a
              </label>
              <select
                data-testid="catalog-field-role"
                value={form.role}
                onChange={handleChange("role")}
                className="w-full border border-neutral-300 bg-white px-4 py-3 text-sm font-nunito outline-none focus:border-[#c99658] transition-colors"
              >
                <option>Architect</option>
                <option>Builder / Contractor</option>
                <option>Designer</option>
                <option>Homeowner</option>
                <option>Developer</option>
                <option>Other</option>
              </select>
            </div>

            {kind === "windows" && (
              <label
                data-testid="catalog-also-doors"
                className="flex items-start gap-3 p-4 bg-white border border-[#c99658]/25 cursor-pointer hover:border-[#c99658]/60 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={form.alsoDoors}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, alsoDoors: e.target.checked }))
                  }
                  className="mt-1 w-4 h-4 accent-[#c99658] flex-shrink-0"
                  data-testid="catalog-also-doors-input"
                />
                <span className="text-[13px] text-neutral-700 font-nunito leading-[1.55]">
                  <span className="block font-medium text-[#0f2420]">
                    Also send me the 2026 Doors Catalog
                  </span>
                  <span className="text-[12px] text-neutral-600">
                    120+ door designs · cast aluminum, wood, stainless, interior.
                  </span>
                </span>
              </label>
            )}

            <p className="text-[11px] text-neutral-600 font-nunito leading-[1.6]">
              We&rsquo;ll occasionally share product updates. Unsubscribe anytime.
            </p>
            <button
              data-testid="catalog-submit-btn"
              type="submit"
              disabled={submitting}
              className="btn-pill btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  {kind === "windows" && form.alsoDoors
                    ? "Get Both Catalogs"
                    : meta.cta}
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-[0.32em] font-raleway text-neutral-600 mb-2">
        {label}
        {required && <span className="text-[#c99658] ml-1">*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full border border-neutral-300 bg-white px-4 py-3 text-sm font-nunito outline-none focus:border-[#c99658] transition-colors"
      />
    </div>
  );
}
